//@ts-check
import * as ramda from 'ramda';
import * as react from 'react';
import * as reactDom from 'react-dom';
import * as recompose from 'recompose';
import moment from 'moment';
import backbone from 'backbone';
import * as jquery from 'jquery';
import lodash from 'lodash';
import {initializeModules} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/modules';
import {fetchJson} from '@sinclair-digital/sbg-util/dist/es/fetch';
import {create, remove} from '@sinclair-digital/sbg-util/dist/es/element';

const {mergeDeepLeft, identity, map, path} = ramda;

/**
 * Wraps a collection of export functions as if it were a esModule
 *
 * @param {Object} moduleExports
 * @returns {Object}
 */
const wrapAsEsModules = (moduleExports) => {
    return Object.defineProperties(
        {...moduleExports},
        {
            '__esModule': {
                value: true,
            },
            [Symbol.toStringTag]: {
                value: 'Module',
            },
        }
    );
};

initializeModules({
    dependencies: {
        backbone,
        jquery,
        lodash,
        moment,
        ramda: wrapAsEsModules(ramda),
        react: wrapAsEsModules(react),
        reactDom: wrapAsEsModules(reactDom),
        recompose: wrapAsEsModules(recompose),
    },
});

const moduleRegistry = '/resources/storyline/pluginRegistry.json';

export const getPluginRegistry = () => fetchJson(moduleRegistry);

export const getPluginMetadata = (plugins = []) => {
    return getPluginRegistry()
        .then(mergeDeepLeft(JSON.parse(localStorage.getItem('localRegistry') || '{}')))
        .then(registry => plugins.map(plugin => {
            const moduleFromRegistry = path(['plugins', plugin.plugin], registry);
            if (moduleFromRegistry){
                return mergeDeepLeft(plugin, moduleFromRegistry);
            } else {
                return undefined;
            }
        }).filter(Boolean));
};

export const getPluginData = name => {
    const script = document.getElementById(`storylineModuleImport-${name}`);
    if (!script) {
        return null;
    }

    return {
        plugin: script.getAttribute('data-name') || name,
        version: script.getAttribute('data-version'),
    };
};

export const loadPlugin = ({name, source, current, version = current} = {}) => {
    if (name && source && version) {
        return new Promise((resolve, reject) => {
            // TODO: sources should load according to their manifest - ADM 5.22.18
            const pluginScript = create('script', {
                src: `${source}/${version}/${name}.bundle.js`,
                id: `storylineModuleImport-${name}`,
                'data-name': name,
                'data-version': version,
            });

            pluginScript.onload = resolve;
            pluginScript.onerror = reject;

            const old = document.getElementById(pluginScript.id);
            if (old) {
                remove(old);
            }
            document.body.appendChild(pluginScript);
        });
    }

    return Promise.reject(
        new Error(`Must specify plugin source, name, and, version for load. Received ${source} ${name} ${version}`),
    );
};

const catchAll = (msg, defaultValue) =>
    map(promise =>
        promise.catch(err => {
            // eslint-disable-next-line no-console
            console.error(msg, err);
            return defaultValue;
        }),
    );

export const tryLoadPlugins = catchLoadErrors => plugins =>
    getPluginMetadata(plugins)
        .then(map(loadPlugin))
        .then(catchLoadErrors ? catchAll('Plugin load failed') : identity)
        .then(Promise.all.bind(Promise));

export const loadPlugins = tryLoadPlugins(false);

export {
    default,
    modules,
    getComponentsForModule,
    getVisibleModuleButtons,
    getModuleButtons,
    getModuleRoutes,
    getModules,
    getSiteMapActions,
    showModule,
    hideModule,
    loadModulesForUser,
    listenForModuleUpdates,
    removeListener,
    registerModule,
    removeModule,
    setModuleRoutes,
} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/modules';
